// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase';
import { 
  onAuthStateChanged, 
  signOut, 
  signInWithEmailAndPassword,
  sendPasswordResetEmail 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Constants
const ADMIN_EMAIL = "installs@humbertrack.co.uk";
const VALID_ROLES = ['admin', 'engineer'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // -----------------------------------------------
  // FETCH USER PROFILE AND ROLE
  // -----------------------------------------------
  const fetchUserProfile = useCallback(async (currentUser) => {
    try {
      setError(null);

      // Admin email always gets admin role
      if (currentUser.email === ADMIN_EMAIL) {
        setUserRole('admin');
        setUserProfile({
          email: currentUser.email,
          displayName: currentUser.displayName || 'Admin',
          uid: currentUser.uid,
        });
        return;
      }

      // Fetch user document from Firestore
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User profile not found. Please contact an administrator.');
      }

      const userData = userDoc.data();

      // Validate role
      if (!VALID_ROLES.includes(userData.role)) {
        throw new Error('Invalid user role assigned. Please contact an administrator.');
      }

      setUserRole(userData.role);
      setUserProfile({
        ...userData,
        uid: currentUser.uid,
        email: currentUser.email,
      });

    } catch (err) {
      console.error("Error fetching user profile:", err);
      setError(err.message);
      setUserRole(null);
      setUserProfile(null);
    }
  }, []);

  // -----------------------------------------------
  // AUTH STATE LISTENER
  // -----------------------------------------------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);

      if (currentUser) {
        setUser(currentUser);
        await fetchUserProfile(currentUser);
      } else {
        // Reset all state on logout
        setUser(null);
        setUserRole(null);
        setUserProfile(null);
        setError(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchUserProfile]);

  // -----------------------------------------------
  // LOGIN FUNCTION
  // -----------------------------------------------
  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (err) {
      console.error("Login error:", err);
      
      // User-friendly error messages
      let errorMessage = 'Failed to login. Please try again.';
      
      switch (err.code) {
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection.';
          break;
        default:
          errorMessage = err.message;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // -----------------------------------------------
  // LOGOUT FUNCTION
  // -----------------------------------------------
  const logout = useCallback(async () => {
    try {
      setError(null);
      await signOut(auth);
      return { success: true };
    } catch (err) {
      console.error("Logout error:", err);
      setError('Failed to logout. Please try again.');
      return { success: false, error: err.message };
    }
  }, []);

  // -----------------------------------------------
  // PASSWORD RESET FUNCTION
  // -----------------------------------------------
  const resetPassword = useCallback(async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
      return { 
        success: true, 
        message: 'Password reset email sent. Please check your inbox.' 
      };
    } catch (err) {
      console.error("Password reset error:", err);
      
      let errorMessage = 'Failed to send reset email.';
      
      switch (err.code) {
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email.';
          break;
        default:
          errorMessage = err.message;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // -----------------------------------------------
  // REFRESH USER PROFILE
  // -----------------------------------------------
  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchUserProfile(user);
    }
  }, [user, fetchUserProfile]);

  // -----------------------------------------------
  // CHECK IF USER IS ADMIN
  // -----------------------------------------------
  const isAdmin = useCallback(() => {
    return userRole === 'admin';
  }, [userRole]);

  // -----------------------------------------------
  // CHECK IF USER IS ENGINEER
  // -----------------------------------------------
  const isEngineer = useCallback(() => {
    return userRole === 'engineer';
  }, [userRole]);

  // Context value
  const value = {
    // State
    user,
    userRole,
    userProfile,
    loading,
    error,

    // Functions
    login,
    logout,
    resetPassword,
    refreshProfile,

    // Helpers
    isAdmin,
    isEngineer,
    isAuthenticated: !!user,
    hasRole: !!userRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;